import { parseNumberInput } from "@/lib/parse-number";
import { parseWholeCount } from "@/lib/tool-kit/essay-templates/text";
import {
  DISPOSITION_KEYS,
  type DispositionKey,
  type Dispositions,
  proportionalE,
} from "@/lib/tool-kit/research-ops/aapor";

/** Reading the typed fields of the response-rate calculator. */

export type FieldKey = DispositionKey | "ineligible";
export type Fields = Record<FieldKey, string>;

const PERCENT_SIGN = /%/g;

/** "0.55" or "55%" as 0.55. Blank is not an error; out of range or unreadable is. */
export function parseProportion(raw: string): {
  value: number | null;
  bad: boolean;
} {
  const text = raw.trim();
  if (text === "") {
    return { value: null, bad: false };
  }
  const parsed = parseNumberInput(text.replace(PERCENT_SIGN, ""));
  if (parsed === null) {
    return { value: null, bad: true };
  }
  const value = text.includes("%") ? parsed / 100 : parsed;
  if (value < 0 || value > 1) {
    return { value: null, bad: true };
  }
  return { value, bad: false };
}

export interface ReadCounts {
  dispositions: Dispositions;
  /** Fields that held something that is not a whole number of cases. */
  bad: FieldKey[];
  /** The ineligible count, or null if blank or unreadable. */
  ineligible: number | null;
}

function wholeCount(text: string): number | null | "blank" {
  const trimmed = text.trim();
  if (trimmed === "") {
    return "blank";
  }
  const value = parseWholeCount(trimmed);
  return value === null || value < 0 ? null : value;
}

export function readCounts(fields: Fields): ReadCounts {
  const dispositions = {} as Dispositions;
  const bad: FieldKey[] = [];
  for (const key of DISPOSITION_KEYS) {
    const value = wholeCount(fields[key]);
    if (value === null) {
      bad.push(key);
    }
    dispositions[key] = typeof value === "number" ? value : 0;
  }
  const ineligible = wholeCount(fields.ineligible);
  if (ineligible === null) {
    bad.push("ineligible");
  }
  return {
    dispositions,
    bad,
    ineligible: typeof ineligible === "number" ? ineligible : null,
  };
}

export interface ResolvedE {
  e: number | null;
  /** Where e came from: the researcher, the ineligible count, or nowhere yet. */
  source: "entered" | "proportional" | "none";
  /** The typed e was unreadable or outside 0–1. */
  bad: boolean;
}

export function resolveE(eText: string, counts: ReadCounts): ResolvedE {
  const entered = parseProportion(eText);
  if (entered.value !== null) {
    return { e: entered.value, source: "entered", bad: false };
  }
  const estimate =
    counts.ineligible === null
      ? null
      : proportionalE(counts.dispositions, counts.ineligible);
  if (estimate !== null) {
    return { e: estimate, source: "proportional", bad: entered.bad };
  }
  return { e: null, source: "none", bad: entered.bad };
}

/** Known-status eligible cases: I + P + R + NC + O. */
export function eligibleCases(d: Dispositions): number {
  return d.I + d.P + d.R + d.NC + d.O;
}
